import { motion } from "framer-motion";
import {
  Brain,
  Globe,
  Smartphone,
  Cloud,
  Layers,
  Building2,
  Zap,
  TrendingUp,
  BrainCircuit,
  CloudLightning,
  Grid,
  ShieldCheck,
  Settings,
  Sparkles,
} from "lucide-react";

// Persistent animations
import AnimatedBackground from "../components/AnimatedBackground";
import ParticleSystem from "../components/ParticleSystem";
import NeuralNetwork from "../components/NeuralNetwork";

// Core components
import Hero from "../components/Hero";
import ServiceCard from "../components/ServiceCard";
import FeatureCard from "../components/FeatureCard";
import Stats from "../components/Stats";
import CTA from "../components/CTA";
import Footer from "../components/Footer";
import XRSection from "../components/XRSection";

// Mock data / local list of services
const servicesList = [
  {
    icon: Brain,
    title: "Artificial Intelligence",
    description: "Custom machine learning models, natural language processing, predictive analysis, and generative AI pipelines.",
    color: "#7C4DFF",
  },
  {
    icon: Globe,
    title: "Web Development",
    description: "Premium, ultra-fast, and responsive SaaS platforms, corporate web systems, and interactive client portals.",
    color: "#27B8FF",
  },
  {
    icon: Smartphone,
    title: "Mobile Apps",
    description: "Cross-platform iOS and Android apps built with modern frameworks for seamless performance and offline capability.",
    color: "#A78BFA",
  },
  {
    icon: Cloud,
    title: "Cloud Solutions",
    description: "Scalable cloud native infrastructure, automated CI/CD pipelines, container orchestration, and serverless compute.",
    color: "#60A5FA",
  },
  {
    icon: Layers,
    title: "UI UX",
    description: "Premium user interfaces, sleek motion design, dynamic wireframes, and unified enterprise design systems.",
    color: "#F472B6",
  },
  {
    icon: Building2,
    title: "Enterprise Software",
    description: "Custom ERPs, CRMs, document automation systems, and high-security transactional business platforms.",
    color: "#10B981",
  },
  {
    icon: Zap,
    title: "Automation",
    description: "Trigger-based workflows, self-healing data pipelines, automated reporting, and smart task scheduling.",
    color: "#EC4899",
  },
  {
    icon: TrendingUp,
    title: "Digital Marketing",
    description: "Data-driven SEO optimization, conversion rate enhancement, visual branding, and analytics pipelines.",
    color: "#F59E0B",
  },
];

// Local list of features
const featuresList = [
  {
    icon: BrainCircuit,
    title: "AI Integration",
    description: "Embed model endpoints, cognitive reasoning loops, and agentic workflows into your existing platform layers.",
    color: "#7C4DFF",
    layout: "lg:col-span-2 lg:translate-y-6",
  },
  {
    icon: CloudLightning,
    title: "Cloud Deployment",
    description: "Auto-scalable, low-latency node configurations deployed across secure multi-cloud architectures.",
    color: "#27B8FF",
    layout: "lg:col-span-2",
  },
  {
    icon: Grid,
    title: "Scalable Architecture",
    description: "Decoupled microservices, event-driven pipelines, and distributed DB storage designed for 99.99% uptime.",
    color: "#A78BFA",
    layout: "lg:col-span-2 lg:-translate-y-6",
  },
  {
    icon: ShieldCheck,
    title: "Secure APIs",
    description: "AES-256 data payload encryption, strict OAuth2 compliance, and automated threat monitoring gates.",
    color: "#60A5FA",
    layout: "lg:col-span-3 lg:-translate-y-2",
  },
  {
    icon: Settings,
    title: "Automation",
    description: "Eliminate manual routing with trigger-based approval pipelines and live system observability.",
    color: "#F472B6",
    layout: "lg:col-span-3 lg:translate-y-8",
  },
];

const Home = () => {
  return (
    <main className="overflow-hidden" style={{ background: "#060B1F" }}>
      {/* Background Animated Layer */}
      <AnimatedBackground />
      <ParticleSystem />
      <NeuralNetwork />

      {/* Hero Section */}
      <Hero />

      {/* Services Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 tech-grid-dense opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <span className="ai-badge mb-4 inline-flex">
              <Brain className="w-4 h-4 text-ai-glow" />
              Full Suite Solutions
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-5"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              Our Core <span className="gradient-text">Services</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
              We specialize in custom enterprise solutions and advanced AI systems built for modern businesses.
            </p>
          </motion.div>

          {/* Service Cards Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesList.map((service, i) => (
              <ServiceCard
                key={service.title}
                index={i}
                icon={service.icon}
                title={service.title}
                description={service.description}
                color={service.color}
              />
            ))}
          </div>
        </div>

        <div className="section-divider mt-20" />
      </section>

      {/* Extended Reality (AR / VR / XR) Section */}
      <XRSection />

      {/* Why ClientCode AI (Stats) */}
      <Stats />

      {/* Features Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <span className="ai-badge mb-4 inline-flex">
              <Sparkles className="w-4 h-4 text-ai-glow" />
              Capabilities
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-5"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              Next-Gen <span className="gradient-text">Features</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
              Leverage custom modules designed for high scalability, speed, and intelligence.
            </p>
          </motion.div>

          {/* Feature Grid */}
          <div className="grid gap-6 lg:grid-cols-6">
            {featuresList.map((feature, i) => (
              <FeatureCard
                key={feature.title}
                index={i}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                color={feature.color}
                layout={feature.layout}
              />
            ))}
          </div>
        </div>

        <div className="section-divider mt-24" />
      </section>

      {/* Call to Action (CTA) */}
      <CTA />

      {/* Premium Footer */}
      <Footer />
    </main>
  );
};

export default Home;
